import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { useNotification } from '../context/NotificationContext.jsx';
import {
    Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Stack, Card, Chip, IconButton, Divider, Tooltip, CircularProgress,
    Alert, Grid, Paper, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    DeleteOutline as DeleteIcon,
    Block as RevokeIcon,
    ContentCopy as CopyIcon,
    CheckCircle as ActiveIcon,
    RadioButtonUnchecked as PendingIcon,
    WifiOff as OfflineIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { LoadingSpinner, ConfirmModal } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { formatRelativeTime } from '../utils/dateTime';


const STATUS_META = {
    ACTIVE:  { label: 'Active',  color: 'success', Icon: ActiveIcon },
    PENDING: { label: 'Pending', color: 'warning', Icon: PendingIcon },
    OFFLINE: { label: 'Offline', color: 'default', Icon: OfflineIcon },
    IDLE:    { label: 'Idle',    color: 'default', Icon: OfflineIcon },
    REVOKED: { label: 'Revoked', color: 'error',   Icon: RevokeIcon },
};

// ─────────────────────────────────────────────────────────────────────────────
// Token Display Dialog
// ─────────────────────────────────────────────────────────────────────────────
const TokenDialog = ({ open, agentId, token: regToken, onClose }) => {
    const [copied, setCopied] = useState(false);
    const [deployTab, setDeployTab] = useState('run');           // 'run' | 'compose'
    const [persistStorage, setPersistStorage] = useState(false);
    const [hostPath, setHostPath] = useState('/opt/aidas/relay-agent');

    // Smart Platform URL from env — auto-fix localhost for Docker
    const rawBase = (import.meta.env.VITE_API_BASE_URL || '');
    const stripped = rawBase.replace(/\/api\/v1\/?$/, '').replace(/\/+$/, '') || window.location.origin;
    const dockerDefault = stripped
        .replace(/localhost/gi, 'host.docker.internal')
        .replace(/127\.0\.0\.1/gi, 'host.docker.internal');
    const [platformUrl, setPlatformUrl] = useState(dockerDefault);
    const isLocalhost = platformUrl.includes('localhost') || platformUrl.includes('127.0.0.1');

    const tokenStr = regToken || '<token>';

    // Volume strings
    const dataVol = persistStorage ? `${hostPath}/data:/data` : 'relay_data:/data';
    const regVol = persistStorage ? `${hostPath}/registry:/data/registry` : 'relay_registry:/data/registry';

    // docker run command
    const dockerRunCmd = [
        'docker run -d \\',
        '  --name apfv2-relay-agent \\',
        '  --restart unless-stopped \\',
        `  -e PLATFORM_URL=${platformUrl} \\`,
        `  -e AGENT_TOKEN=${tokenStr} \\`,
        `  -v ${dataVol} \\`,
        `  -v ${regVol} \\`,
        '  aidasapps/relay-agent:latest',
    ].join('\n');

    // docker-compose.yml
    const composeLines = [
        'version: "3.9"',
        'services:',
        '  relay-agent:',
        '    image: aidasapps/relay-agent:latest',
        '    container_name: apfv2-relay-agent',
        '    restart: unless-stopped',
        '    environment:',
        `      - PLATFORM_URL=${platformUrl}`,
        `      - AGENT_TOKEN=${tokenStr}`,
        '    volumes:',
        `      - ${dataVol}`,
        `      - ${regVol}`,
        '    network_mode: host',
    ];
    if (!persistStorage) {
        composeLines.push('', 'volumes:', '  relay_data:', '  relay_registry:');
    }
    const composeCmd = composeLines.join('\n');

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleDownloadCompose = () => {
        const blob = new Blob([composeCmd], { type: 'text/yaml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'docker-compose.yml';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <DynamicIcon name="ShieldCheck" className="w-5 h-5 text-green-500" />
                    <span>Agent Created — Save Your Token</span>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    This registration token is shown <strong>only once</strong>. Save it now.
                </Alert>

                {/* Registration Token */}
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Registration Token
                </Typography>
                <Box sx={{
                    fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: 'action.hover',
                    borderRadius: 1, p: 1.5, mb: 2, wordBreak: 'break-all',
                    border: '1px solid', borderColor: 'divider', position: 'relative',
                }}>
                    {regToken}
                    <Tooltip title={copied ? 'Copied!' : 'Copy token'}>
                        <IconButton size="small" onClick={() => handleCopy(regToken)}
                            sx={{ position: 'absolute', top: 4, right: 4 }}>
                            <CopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Platform URL */}
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Platform URL
                    <Typography component="span" variant="caption" color="text.disabled"> (edit if needed)</Typography>
                </Typography>
                <TextField fullWidth size="small" value={platformUrl}
                    onChange={(e) => setPlatformUrl(e.target.value)}
                    sx={{ mb: 1 }}
                    inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
                {isLocalhost && (
                    <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                        Running in Docker? Replace <code>localhost</code> with <code>host.docker.internal</code>.
                    </Alert>
                )}

                {/* Options */}
                <Box sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                    p: 1.5, mb: 2, bgcolor: 'action.hover',
                }}>
                    <Stack direction="row" alignItems="center" spacing={1}
                        onClick={() => setPersistStorage(!persistStorage)}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                        <input type="checkbox" checked={persistStorage}
                            onChange={() => setPersistStorage(!persistStorage)}
                            style={{ accentColor: '#2563eb', width: 16, height: 16 }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                            Persistent storage on host machine
                        </Typography>
                    </Stack>
                    {persistStorage && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, ml: 3.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                📁 Host path:
                            </Typography>
                            <TextField size="small" fullWidth value={hostPath}
                                onChange={(e) => setHostPath(e.target.value)}
                                inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem', padding: '4px 8px' } }}
                                sx={{ '& .MuiOutlinedInput-root': { height: 28 } }}
                            />
                        </Stack>
                    )}
                </Box>

                {/* Deploy Command Header */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1 }} display="block">
                    Deploy Command
                </Typography>

                {/* Tab Switcher */}
                <Stack direction="row" spacing={0} sx={{ mb: 0 }}>
                    {[
                        { key: 'run', label: 'docker run' },
                        { key: 'compose', label: 'docker-compose.yml' },
                    ].map(tab => (
                        <Box
                            key={tab.key}
                            onClick={() => setDeployTab(tab.key)}
                            sx={{
                                px: 2, py: 0.75, cursor: 'pointer',
                                fontSize: '0.75rem', fontWeight: 600, fontFamily: 'monospace',
                                borderRadius: '6px 6px 0 0',
                                bgcolor: deployTab === tab.key ? 'grey.900' : 'action.hover',
                                color: deployTab === tab.key ? 'grey.100' : 'text.secondary',
                                transition: 'all 0.15s ease',
                                border: '1px solid',
                                borderColor: deployTab === tab.key ? 'grey.900' : 'divider',
                                borderBottom: deployTab === tab.key ? 'none' : undefined,
                            }}
                        >
                            {tab.label}
                        </Box>
                    ))}
                </Stack>

                {/* Command Block */}
                <Box sx={{
                    fontFamily: 'monospace', fontSize: '0.72rem', bgcolor: 'grey.900',
                    color: 'grey.100', borderRadius: '0 4px 4px 4px', p: 1.5,
                    whiteSpace: 'pre', overflow: 'auto', maxHeight: 260,
                    position: 'relative', lineHeight: 1.7,
                    border: '1px solid', borderColor: 'grey.800',
                }}>
                    {deployTab === 'run' ? dockerRunCmd : composeCmd}
                </Box>

                {/* Action buttons + Run instructions */}
                {deployTab === 'run' ? (
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                        <Button
                            variant="outlined" size="small" startIcon={<CopyIcon fontSize="small" />}
                            onClick={() => handleCopy(dockerRunCmd)}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.78rem' }}
                        >
                            {copied ? 'Copied!' : 'Copy Command'}
                        </Button>
                        <Alert severity="info" sx={{ fontSize: '0.73rem' }} icon={false}>
                            <strong>Run instructions:</strong> Paste the copied command in your terminal to start the agent.
                        </Alert>
                    </Stack>
                ) : (
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                        <Button
                            variant="outlined" size="small"
                            startIcon={<DynamicIcon name="Download" className="w-4 h-4" />}
                            onClick={handleDownloadCompose}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.78rem' }}
                        >
                            Download docker-compose.yml
                        </Button>
                        <Alert severity="info" sx={{ fontSize: '0.73rem' }} icon={false}>
                            <strong>Run instructions:</strong> Save the downloaded file to your project folder, then run:
                            <Box component="code" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}>
                                docker compose up -d
                            </Box>
                        </Alert>
                    </Stack>
                )}

                {/* Agent ID */}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }} display="block">
                    Agent ID: <code>{agentId}</code>
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">I've saved the token</Button>
            </DialogActions>
        </Dialog>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Agent Card
// ─────────────────────────────────────────────────────────────────────────────
const AgentCard = ({ agent, onRevoke, onDelete, theme }) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const meta = STATUS_META[agent.status] || STATUS_META.OFFLINE;
    const { Icon: StatusIcon } = meta;

    return (
        <Card elevation={0} sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 2,
            transition: 'all 0.18s ease',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4], borderColor: 'primary.main' },
        }}>
            <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{
                        width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                        bgcolor: agent.status === 'ACTIVE' ? 'success.light' : 'action.hover',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <DynamicIcon name="Server" className="w-5 h-5" style={{
                            color: agent.status === 'ACTIVE' ? '#16a34a' : '#94a3b8'
                        }} />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>{agent.name}</Typography>
                        {agent.description && (
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {agent.description}
                            </Typography>
                        )}
                    </Box>
                    <IconButton size="small"
                        onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Chip
                        icon={<StatusIcon sx={{ fontSize: '14px !important' }} />}
                        label={meta.label} size="small" color={meta.color} variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                    <Stack direction="row" spacing={2}>
                        <Typography variant="caption" color="text.secondary">v{agent.version || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">♥ {formatRelativeTime(agent.heartbeat_at)}</Typography>
                    </Stack>
                </Stack>
            </Box>

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                {agent.status !== 'REVOKED' && (
                    <MenuItem onClick={() => { setMenuAnchor(null); onRevoke(agent); }} sx={{ color: 'warning.main' }}>
                        <ListItemIcon><RevokeIcon fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText>Revoke Agent</ListItemText>
                    </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={() => { setMenuAnchor(null); onDelete(agent); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>
        </Card>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Create Agent Dialog
// ─────────────────────────────────────────────────────────────────────────────
const CreateAgentDialog = ({ open, onClose, onCreated, token }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const { addNotification } = useNotification();

    const handleClose = () => { setName(''); setDescription(''); onClose(); };

    const handleCreate = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try {
            const result = await apiFetch('/relay/agents', token, {
                method: 'POST',
                body: JSON.stringify({ name: name.trim(), description: description.trim() || null }),
            });
            onCreated(result);
            handleClose();
        } catch (e) {
            addNotification(e.message || 'Failed to create agent.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Register New Relay Agent</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        label="Agent Name *" size="small" fullWidth
                        value={name} onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. HQ On-Prem Agent"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                    />
                    <TextField
                        label="Description" size="small" fullWidth multiline rows={2}
                        value={description} onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional — what database does this agent connect to?"
                    />
                    <Alert severity="info" sx={{ fontSize: '0.78rem' }}>
                        After creating, you'll receive a one-time registration token to pass to the Docker container.
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button variant="contained" onClick={handleCreate}
                    disabled={saving || !name.trim()}
                    startIcon={saving ? <CircularProgress size={14} /> : null}>
                    Create Agent
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Main Component — Agent Management (single view, no tabs)
// ─────────────────────────────────────────────────────────────────────────────
const RelayAgentsSettings = ({ tenantId, token }) => {
    const theme = useTheme();
    const { addNotification } = useNotification();

    const { data: agents, isLoading, refresh } = useData(
        tenantId ? `/relay/agents?tenant_id=${tenantId}` : '/relay/agents'
    );

    const [createOpen, setCreateOpen] = useState(false);
    const [tokenDialog, setTokenDialog] = useState(null);
    const [confirmRevoke, setConfirmRevoke] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const handleCreated = (result) => {
        setTokenDialog({ agentId: result.agent_id, regToken: result.reg_token });
        refresh();
    };

    const handleRevoke = async () => {
        if (!confirmRevoke) return;
        try {
            await apiFetch(`/relay/agents/${confirmRevoke.id}/revoke`, token, { method: 'PATCH' });
            addNotification(`Agent "${confirmRevoke.name}" revoked.`, 'success');
            refresh();
        } catch (e) {
            addNotification(e.message || 'Failed to revoke.', 'error');
        } finally {
            setConfirmRevoke(null);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await apiFetch(`/relay/agents/${confirmDelete.id}`, token, { method: 'DELETE' });
            addNotification(`Agent "${confirmDelete.name}" deleted.`, 'success');
            refresh();
        } catch (e) {
            addNotification(e.message || 'Failed to delete.', 'error');
        } finally {
            setConfirmDelete(null);
        }
    };

    const agentList = agents || [];
    const activeCount = agentList.filter(a => a.status === 'ACTIVE').length;

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>

            {/* ── Page Header ── */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <DynamicIcon name="Server" className="w-5 h-5 text-indigo-500" />
                        <Typography variant="h5" fontWeight="bold">Relay Agents</Typography>
                        {activeCount > 0 && (
                            <Chip label={`${activeCount} Active`} size="small" color="success"
                                sx={{ height: 20, fontSize: '0.68rem' }} />
                        )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Deploy lightweight agents inside your network to securely extract, sanitize PHI/PII, and upload to S3.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh"><IconButton onClick={refresh} size="small"><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                    <Button variant="contained" startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)} id="btn-register-relay-agent">
                        Register Agent
                    </Button>
                </Stack>
            </Stack>

            {/* ── How it works + Prerequisites ── */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                <Box sx={{
                    bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200',
                    borderRadius: 2, p: 2, flex: 1,
                    display: 'flex', gap: 2, alignItems: 'flex-start',
                }}>
                    <DynamicIcon name="Info" className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue-500" />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>How Relay Agents Work</Typography>
                        <Stack spacing={0.5}>
                            {[
                                '1. Register an agent here — you receive a one-time token.',
                                '2. Run the relay-agent Docker image in your network with the token.',
                                '3. The agent registers, then polls for jobs every 30 seconds.',
                                '4. When a Data Model with PD columns refreshes, the agent extracts, sanitizes, and uploads clean Parquet to S3.',
                                '5. Configure Protected Data (PHI) mappings in the Data Model Editor → Step 4: Protect Data.',
                            ].map((step, i) => (
                                <Typography key={i} variant="caption" color="text.secondary">{step}</Typography>
                            ))}
                        </Stack>
                    </Box>
                </Box>
                <Box sx={{
                    bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200',
                    borderRadius: 2, p: 2, minWidth: 260,
                    display: 'flex', gap: 2, alignItems: 'flex-start',
                }}>
                    <DynamicIcon name="ShieldCheck" className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-600" />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Prerequisites</Typography>
                        <Stack spacing={0.5}>
                            {[
                                { icon: '✅', text: 'Docker installed on target machine' },
                                { icon: '✅', text: 'Outbound HTTPS (port 443) to platform' },
                                { icon: '✅', text: 'Network access to on-premise database' },
                                { icon: '❌', text: 'No inbound ports required' },
                                { icon: '❌', text: 'No AWS credentials needed' },
                            ].map((item, i) => (
                                <Typography key={i} variant="caption" color="text.secondary">
                                    {item.icon} {item.text}
                                </Typography>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </Stack>

            {/* ── Agent Grid ── */}
            {isLoading ? <LoadingSpinner /> : agentList.length === 0 ? (
                <Box sx={{
                    textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 2,
                    border: '1px dashed', borderColor: 'divider',
                }}>
                    <DynamicIcon name="Server" className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>No agents registered</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Register your first relay agent to start extracting on-premise data.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)} id="btn-register-first-relay-agent">
                        Register an Agent
                    </Button>

                </Box>
            ) : (
                <Grid container spacing={2}>
                    {agentList.map((agent) => (
                        <Grid item xs={12} sm={6} md={4} key={agent.id}>
                            <AgentCard
                                agent={agent} theme={theme}
                                onRevoke={setConfirmRevoke}
                                onDelete={setConfirmDelete}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* ── Dialogs ── */}
            <CreateAgentDialog open={createOpen} onClose={() => setCreateOpen(false)}
                onCreated={handleCreated} token={token} />

            <TokenDialog open={Boolean(tokenDialog)} agentId={tokenDialog?.agentId}
                token={tokenDialog?.regToken} onClose={() => setTokenDialog(null)} />

            <ConfirmModal isOpen={Boolean(confirmRevoke)} onClose={() => setConfirmRevoke(null)}
                onConfirm={handleRevoke}
                title={`Revoke "${confirmRevoke?.name}"?`}
                message="The agent will be blocked from polling for new jobs. Any running jobs will complete." />

            <ConfirmModal isOpen={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}
                onConfirm={handleDelete}
                title={`Delete "${confirmDelete?.name}"?`}
                message="This agent and all its job history will be permanently deleted. This cannot be undone." />
        </Box>
    );
};

export default RelayAgentsSettings;
